// src/lib/services/activityService.ts
// Unified activity tracking service - tracks all user activities under their uid

import { db } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';

export type ActivityType =
  | 'video_watch'
  | 'video_upload'
  | 'short_watch'
  | 'short_upload'
  | 'live_host'
  | 'live_join'
  | 'event_host'
  | 'event_attend'
  | 'studio_export'
  | 'studio_create'
  | 'comment'
  | 'like'
  | 'search'
  | 'auth.login'
  | 'auth.logout'
  | 'user.signup'
  | 'live_end'
  | 'video_delete'
  | 'admin.ban_user';

export interface Activity {
  id?: string;
  userId: string;
  type: ActivityType;
  title: string;
  description?: string;
  metadata?: Record<string, any>;
  createdAt: Timestamp | Date;
}

const ACTIVITY_COLLECTION = 'userActivity';

/**
 * Record a user activity
 */
export async function recordActivity(
  userId: string,
  type: ActivityType,
  title: string,
  description?: string,
  metadata?: Record<string, any>
): Promise<string> {
  if (!userId) return '';
  try {
    const activityRef = collection(db, ACTIVITY_COLLECTION);
    const docRef = await addDoc(activityRef, {
      userId,
      type,
      title,
      description: description || '',
      metadata: metadata || {},
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    // Activity recording is non-critical — log but don't throw so callers aren't disrupted
    console.error('Error recording activity:', error);
    return '';
  }
}

/**
 * Get recent activities for a user
 */
export async function getUserActivities(
  userId: string,
  maxItems = 20
): Promise<Activity[]> {
  try {
    const activityRef = collection(db, ACTIVITY_COLLECTION);
    const q = query(
      activityRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );

    const snap = await getDocs(q);
    return snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Activity[];
  } catch (error) {
    console.error('Error fetching user activities:', error);
    return [];
  }
}

/**
 * Get activities by type for a user
 */
export async function getUserActivitiesByType(
  userId: string,
  type: ActivityType,
  maxItems = 10
): Promise<Activity[]> {
  try {
    const activityRef = collection(db, ACTIVITY_COLLECTION);
    const q = query(
      activityRef,
      where('userId', '==', userId),
      where('type', '==', type),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );

    const snap = await getDocs(q);
    return snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Activity[];
  } catch (error) {
    console.error('Error fetching user activities by type:', error);
    return [];
  }
}

// --- Convenience functions for common activity types ---

export async function recordVideoWatch(
  userId: string,
  videoId: string,
  videoTitle: string,
  watchDuration: number
): Promise<void> {
  await recordActivity(userId, 'video_watch', `Watched: ${videoTitle}`, undefined, {
    videoId,
    watchDuration,
  });
}

export async function recordStudioExport(
  userId: string,
  projectName: string,
  exportType: string
): Promise<void> {
  await recordActivity(userId, 'studio_export', `Exported: ${projectName}`, undefined, {
    exportType,
  });
}

export async function recordLiveJoin(
  userId: string,
  eventId: string,
  eventTitle: string
): Promise<void> {
  await recordActivity(userId, 'live_join', `Joined live: ${eventTitle}`, undefined, {
    eventId,
  });
}

export async function recordLiveHost(
  userId: string,
  eventId: string,
  eventTitle: string
): Promise<void> {
  await recordActivity(userId, 'live_host', `Hosted live: ${eventTitle}`, undefined, {
    eventId,
  });
}

export async function recordEventAttend(
  userId: string,
  eventId: string,
  eventTitle: string
): Promise<void> {
  await recordActivity(userId, 'event_attend', `Attended: ${eventTitle}`, undefined, {
    eventId,
  });
}
